import React from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import { ACTIVE } from "../constant/constant";
import emptyImg from "../assets/images/empty.png";
import moment from "moment";

/**
 * ##Protected Route Components
 * @returns
 */
export const PrivateRoutes = () => {
  const auth = localStorage.getItem("token");
  return auth ? (
    <>
      <Navigate to={"/dashboard"} />
      <Outlet />
    </>
  ) : (
    <Navigate to={"/login"} />
  );
};

export const NewPrivateRoute = ({ children, ...rest }) => {
  const auth = localStorage.getItem("token");
  return <Route {...rest}>{auth ? children : <Navigate to="/login" />}</Route>;
};

/**
 * ##Validation Message Handling
 * @param {*} param0
 * @returns
 */
export const ValidationMessage = ({ error, name }) => {
  var message = "";
  if (error && typeof error === "object" && Object.prototype.hasOwnProperty.call(error, name)) {
    const fieldError = error[name];
    if (Array.isArray(fieldError) && fieldError.length > 0) {
      message = fieldError[0];
    } else if (typeof fieldError === "string") {
      message = fieldError;
    }
  }
  return message ? <span className="text-danger">{message}</span> : null;
};

/**
 * ## Pagination Component
 * @param {*} param
 * @returns
 */

export const Pagination = ({ paginator, page, perPage, setPerPage }) => {
  if (!paginator) return null;

  const displayRange = 5;
  const halfRange = Math.floor(displayRange / 2);

  const current = paginator.current;
  const last = paginator.last;
  const total = paginator.total;

  // Calculate items per page from total/last
  const itemsPerPage = perPage || Math.ceil(total / last);

  // Calculate the "Showing X to Y of Z" range
  const from = total === 0 ? 0 : (current - 1) * itemsPerPage + 1;
  const to = total === 0 ? 0 : Math.min(current * itemsPerPage, total);

  const startPage = Math.max(1, current - halfRange);
  const endPage = Math.min(last, startPage + displayRange - 1);

  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPerPage && setPerPage(newPerPage);
    page(1); // reset to first page when rows per page changes
  };

  return (
    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
      {/* Left side text */}
      <div className="ms-3 mb-2">
        <span>
          Showing {from} to {to} of {total} entries
        </span>
      </div>

      {/* Right side pagination & dropdown */}
      <div className="d-flex align-items-center mb-2">

        {/* Rows per page dropdown */}
        <div className="me-3">
          <label className="me-2">Show</label>
          <select
            className="form-select d-inline-block w-auto"
            value={itemsPerPage}
            onChange={handlePerPageChange}
          >
            {[10, 25, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <span className="ms-2">rows per page</span>
        </div>
        {/* Rows per page dropdown */}

        <nav aria-label="Page navigation mt-3">
          <ul className="pagination justify-content-end me-3 mt-3">
            <li
              className="page-item prev"
              onClick={() => page(Math.max(1, paginator.current - 1))}
            >
              <div className="page-link">
                <i className="tf-icon bx bx-chevrons-left"></i>
              </div>
            </li>
            {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
              <li
                key={startPage + i}
                onClick={() => page(startPage + i)}
                className={
                  paginator.current === startPage + i ? "page-item active" : "page-item"
                }
              >
                <div className="page-link">{startPage + i}</div>
              </li>
            ))}
            <li
              className="page-item next"
              onClick={() => page(Math.min(paginator.last, paginator.current + 1))}
            >
              <div className="page-link">
                <i className="tf-icon bx bx-chevrons-right"></i>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};
/**
 * ## set badge class active while data status is Active
 * @param {*} status
 * @returns
 */
export const setBadgeClass = (status) => {
  if (status === ACTIVE) return "badge bg-label-info me-1";
  return "badge bg-label-warning me-1";
};

/**
 * Table Data Not Found means show the message
 * @param {*} param0
 * @returns
 */
// export const NoDataFound = ({ front, back, message }) => {
//   return (
//     <tr>
//       <td colSpan={front}> </td>
//       <td>{message}</td>
//       <td colSpan={back}> </td>
//     </tr>
//   );
// };

export const NoDataFound = ({ colspan, message }) => {
  return (
    <tr>
      <td className="center" colSpan={colspan}>{message}</td>
    </tr>
  );
};

export const NoUserFound = ({ title = "No data found", subtitle = "There is no data available to show at this time." }) => {
  return (
    <div className="not-found-container" style={{ textAlign: "center", padding: "20px 20px 40px" }}>
      <div className="not-found-icon-wrapper" style={{ marginBottom: "16px" }}>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block" }}>
          <g transform="rotate(-15 36 43)">
            <rect x="18" y="22" width="36" height="42" rx="8" fill="#F1F5F9" />
          </g>
          <g transform="rotate(10 48 37)" style={{ filter: "drop-shadow(0px 8px 16px rgba(100, 116, 139, 0.15))" }}>
            <rect x="30" y="16" width="36" height="42" rx="8" fill="#FFFFFF" />
            <rect x="38" y="30" width="20" height="5" rx="2.5" fill="#E2E8F0" />
            <rect x="38" y="40" width="12" height="5" rx="2.5" fill="#E2E8F0" />
          </g>
        </svg>
      </div>
      <h5 style={{ color: "#CF2030", fontWeight: "700", fontSize: "1.125rem", marginBottom: "8px" }}>
        {title}
      </h5>
      <p style={{ color: "#333", fontSize: "0.875rem", margin: 0, marginLeft: "auto", marginRight: "auto", lineHeight: "1.6" }}>
        {subtitle}
      </p>
    </div>
  );
};

/**
 * frame the react select options
 * @param {*} data
 * @param {*} key
 * @param {*} value
 * @returns
 */
export const frameDataOptions = (data, key, value) => {
  if (data.length > 0) {
    return data.map((rs) => ({ value: rs[key], label: rs[value] }));
  }
  return [];
};

/**
 * selected the react-select value pluck
 * @param {*} data
 * @returns
 */
export const pluckValue = (data) => {
  let response = [];
  if (data.length > 0) {
    data.forEach((value) => response.push(value.value));
  }
  return response;
};

export const checkEmptyOrNull = (data) => {
  return data !== "" && data !== null && data !== undefined ? data : "-";
};

export const weekDaysInText = (day) => {
  let day_text = "";
  if (day === 0) day_text = "Sunday";
  else if (day === 1) day_text = "Monday";
  else if (day === 2) day_text = "Tuesday";
  else if (day === 3) day_text = "Wednesday";
  else if (day === 4) day_text = "Thursday";
  else if (day === 5) day_text = "Friday";
  else if (day === 6) day_text = "Saturday";
  return day_text;
}

export const meetingWeeksInText = (week) => {
  let week_text = "";
  if (week === 1) week_text = "1st Week";
  else if (week === 2) week_text = "2nd Week";
  else if (week === 3) week_text = "3rd Week";
  else if (week === 4) week_text = "4th Week";
  else if (week === 5) week_text = "5th Week";
  return week_text;
}

export const WrapText = (text, maxLength) => {
  const words = text.split(' ');

  let currentLength = 0;
  let wrappedText = [];

  for (const word of words) {
    if (currentLength + word.length > maxLength) {
      wrappedText.push(<br key={wrappedText.length} />);
      currentLength = 0;
    }

    wrappedText.push(word + ' ');
    currentLength += word.length + 1; // 1 is for the space between words
  }

  return wrappedText;
};

export const hasRole = (roles) => {
  if (!Array.isArray(roles)) return false;
  const auth_user = JSON.parse(localStorage.getItem("user"));
  const userType = Number(auth_user?.user_type);
  if (!userType) return false;
  return roles.map(Number).includes(userType);
}

export const getRoleName = (user_type) => {
  if (user_type === 1) {
    return "Super Admin";
  } else if (user_type === 6) {
    return "Country Admin";
  } else if (user_type === 7) {
    return "State Admin";
  } else if (user_type === 8) {
    return "City Admin";
  } else if (user_type === 2) {
    return "Region Admin";
  } else if (user_type === 3) {
    return "Circle Admin";
  } else if (user_type === 4) {
    return "Member";
  } else {
    return "";
  }
}

export const getOrdinalSuffix = (i) => {
  const j = i % 10,
        k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
};

export const renderMemberLogTypeBadge = (type) => {
  const typeVal = Number(type);
  if (typeVal === 1 || type === "New Registration") {
    return <span className="badge bg-primary">New Registration</span>;
  }
  if (typeVal === 2 || type === "Break Membership") {
    return <span className="badge bg-danger">Break Membership</span>;
  }
  if (typeVal === 3 || type === "Rejoining") {
    return <span className="badge bg-success">Rejoining</span>;
  }
  if (typeVal === 4 || type === "Category Change" || type === "category_change") {
    return <span className="badge bg-info">Category Change</span>;
  }
  if (typeVal === 0 || type === "membership_status" || type === "Membership Status") {
    return <span className="badge bg-secondary">Membership Status</span>;
  }
  return <span className="badge bg-secondary">{type || "-"}</span>;
};

export const getMemberBreakPeriodText = (log, index, allLogs) => {
  const typeVal = Number(log.type);
  if (typeVal === 2 || log.type === "Break Membership") {
    const newerRejoin = allLogs?.slice(0, index).reverse().find(l => Number(l.type) === 3 || l.type === "Rejoining");
    if (newerRejoin && newerRejoin.date_time && log.date_time) {
      const start = moment(log.date_time);
      const end = moment(newerRejoin.date_time);
      if (start.isValid() && end.isValid()) {
        const isSameDay = start.isSame(end, "day");
        const diffDays = Math.abs(end.clone().startOf("day").diff(start.clone().startOf("day"), "days"));
        const durationText = !isSameDay && diffDays > 0 ? ` (${diffDays} ${diffDays === 1 ? "day" : "days"})` : "";
        return `Break: ${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}${durationText}`;
      }
    }
    return `On break since ${log.date_time ? moment(log.date_time).format("DD-MM-YYYY") : "-"}`;
  }
  if (typeVal === 3 || log.type === "Rejoining") {
    const olderBreak = allLogs?.slice(index + 1).find(l => Number(l.type) === 2 || l.type === "Break Membership");
    if (olderBreak && olderBreak.date_time && log.date_time) {
      const start = moment(olderBreak.date_time);
      const end = moment(log.date_time);
      if (start.isValid() && end.isValid()) {
        const isSameDay = start.isSame(end, "day");
        const diffDays = Math.abs(end.clone().startOf("day").diff(start.clone().startOf("day"), "days"));
        const durationText = !isSameDay && diffDays > 0 ? ` (${diffDays} ${diffDays === 1 ? "day" : "days"})` : "";
        return `Break Period: ${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}${durationText}`;
      }
    }
  }
  return null;
};